import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, UserRound } from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { WikiPageForm } from '../../components/wiki/WikiPageForm';
import { CreateWikiPageDto } from '../../types/wiki';
import { stakeholderService } from '../../services/stakeholderService';
import { organizationService } from '../../services/organizationService';
import { Stakeholder } from '../../types/stakeholder';
import { Organization } from '../../types/organization';
import { wikiService } from '../../services/wikiService';
import { Button, Card, ENTITY_TONE, Field, Page, PageHeader, SearchInput, SegmentedControl, Select } from '../../components/ui';

type EntityType = 'stakeholder' | 'organization';

const ENTITY_TYPES: { value: EntityType; label: React.ReactNode }[] = [
  {
    value: 'stakeholder',
    label: (
      <>
        <UserRound className={`h-3.5 w-3.5 ${ENTITY_TONE.stakeholder.text}`} strokeWidth={1.75} aria-hidden="true" />
        Stakeholder
      </>
    ),
  },
  {
    value: 'organization',
    label: (
      <>
        <Building2 className={`h-3.5 w-3.5 ${ENTITY_TONE.organization.text}`} strokeWidth={1.75} aria-hidden="true" />
        Organization
      </>
    ),
  },
];

export const WikiCreatePage: React.FC = () => {
  const navigate = useNavigate();

  const [entityType, setEntityType] = useState<'stakeholder' | 'organization' | ''>('');
  const [entityId, setEntityId] = useState('');

  const [stakeholderQuery, setStakeholderQuery] = useState('');
  const [organizationQuery, setOrganizationQuery] = useState('');
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initial load of small lists to choose from
    void loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntities = async () => {
    try {
      setLoadingEntities(true);
      setError(null);
      const [s, o] = await Promise.all([
        stakeholderService.getStakeholders({ page: 1, limit: 20, search: stakeholderQuery || undefined }),
        organizationService.getOrganizations({ page: 1, limit: 20, search: organizationQuery || undefined }),
      ]);
      setStakeholders(s.data || []);
      setOrganizations(o.data || []);
    } catch (err) {
      setError('Failed to load entities');
    } finally {
      setLoadingEntities(false);
    }
  };


  const handleCreate = async (data: CreateWikiPageDto) => {
    const created = await wikiService.createWikiPage(data);
    navigate(`/wiki/${created.id}`);
  };

  const isStakeholder = entityType === 'stakeholder';
  const query = isStakeholder ? stakeholderQuery : organizationQuery;
  const setQuery = isStakeholder ? setStakeholderQuery : setOrganizationQuery;

  return (
    <DashboardLayout>
      <Page>
        <div className="mx-auto max-w-[768px]">
          <PageHeader back={{ to: '/wiki', label: 'Wiki' }} title="New page" />

          {/* Entity selection */}
          <Card padded className="space-y-4">
            <Field label="Belongs to">
              <SegmentedControl
                label="Entity type"
                items={ENTITY_TYPES}
                value={entityType as EntityType}
                onChange={(value) => { setEntityType(value); setEntityId(''); }}
              />
            </Field>

            {entityType && (
              <div className="grid grid-cols-1 animate-fade-up gap-3 sm:grid-cols-2">
                <Field label="Find" htmlFor="wiki-entity-search">
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => { e.preventDefault(); void loadEntities(); }}
                  >
                    <SearchInput
                      id="wiki-entity-search"
                      className="min-w-0 flex-1"
                      placeholder={isStakeholder ? 'Search stakeholders' : 'Search organizations'}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    <Button type="submit" variant="secondary" className="h-10 sm:h-9" loading={loadingEntities}>
                      Search
                    </Button>
                  </form>
                </Field>

                <Field label={isStakeholder ? 'Stakeholder' : 'Organization'} htmlFor="wiki-entity">
                  <Select id="wiki-entity" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                    {isStakeholder ? (
                      <>
                        <option value="">Select stakeholder…</option>
                        {stakeholders.map(s => (
                          <option key={s.id} value={s.id}>{s.firstName} {s.lastName} {s.professionalEmail ? `(${s.professionalEmail})` : s.personalEmail ? `(${s.personalEmail})` : ''}</option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="">Select organization…</option>
                        {organizations.map(o => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </>
                    )}
                  </Select>
                </Field>
              </div>
            )}

            {error && (
              <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>
            )}
          </Card>

          {/* Wiki form (only after entity selection) */}
          {entityType && entityId && (
            <WikiPageForm
              className="mt-5"
              entityType={entityType}
              entityId={entityId}
              onSubmit={(data) => handleCreate(data as CreateWikiPageDto)}
              onCancel={() => navigate('/wiki')}
            />
          )}
        </div>
      </Page>
    </DashboardLayout>
  );
};
